import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Building2, 
  Clock, 
  DollarSign, 
  Calendar,
  CheckCircle,
  AlertCircle,
  XCircle,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Transfers() {
  const [loading, setLoading] = useState(true);
  const [bankAccount, setBankAccount] = useState(null);
  const [pendingBalance, setPendingBalance] = useState(null);
  const [transferHistory, setTransferHistory] = useState([]);
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadTransferData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTransferData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Cargar datos en paralelo
      const [accountRes, balanceRes, historyRes, pendingRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/merchant/bank-account`, { headers }),
        axios.get(`${BACKEND_URL}/api/merchant/pending-balance`, { headers }),
        axios.get(`${BACKEND_URL}/api/merchant/transfers/history?limit=50`, { headers }),
        axios.get(`${BACKEND_URL}/api/merchant/transfers/pending`, { headers })
      ]);

      setBankAccount(accountRes.data.bank_account);
      setPendingBalance(balanceRes.data);
      setTransferHistory(historyRes.data || []);
      setPendingTransfers(pendingRes.data || []);
    } catch (error) {
      console.error('Error loading transfer data:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'VES',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-VE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Completada' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock, label: 'Pendiente' },
      processing: { bg: 'bg-blue-100', text: 'text-blue-800', icon: RefreshCw, label: 'Procesando' },
      failed: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle, label: 'Fallida' },
      retrying: { bg: 'bg-orange-100', text: 'text-orange-800', icon: RefreshCw, label: 'Reintentando' }
    };

    const style = styles[status] || styles.pending;
    const Icon = style.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
        <Icon className="w-3 h-3" />
        {style.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: '#003594' }} />
          <p className="text-gray-600">Cargando información de transferencias...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl font-bold" style={{ color: '#051C2C' }}>
                  Transferencias
                </h1>
                <p className="text-gray-600 mt-1">Gestiona tus dispersiones de fondos</p>
              </div>
            </div>
            <button
              onClick={loadTransferData}
              className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:opacity-90"
              style={{ backgroundColor: '#003594' }}
            >
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Balance Pendiente */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 rounded-lg" style={{ backgroundColor: '#E6F0FF' }}>
                <DollarSign className="w-6 h-6" style={{ color: '#003594' }} />
              </div>
              <div>
                <p className="text-sm text-gray-600">Balance Pendiente</p>
                <p className="text-2xl font-bold" style={{ color: '#003594' }}>
                  {pendingBalance ? formatCurrency(pendingBalance.pending_balance) : '---'}
                </p>
              </div>
            </div>
            {pendingBalance && (
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center gap-2 text-sm">
                  {pendingBalance.will_transfer_next_cycle ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-green-700">Se transferirá en el próximo ciclo</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                      <span className="text-amber-700">
                        Mínimo: {formatCurrency(pendingBalance.minimum_transfer_amount)}
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Próximas Dispersiones */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 rounded-lg bg-blue-50">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Próximas Dispersiones</p>
                <p className="text-lg font-semibold text-gray-900">4 veces al día</p>
              </div>
            </div>
            {pendingBalance?.next_dispersion_times && (
              <div className="mt-4 pt-4 border-t">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {pendingBalance.next_dispersion_times.map((time) => (
                    <div key={time} className="flex items-center gap-2 text-gray-700">
                      <Calendar className="w-3 h-3 text-gray-400" />
                      {time}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Transferencias Pendientes */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 rounded-lg bg-amber-50">
                <RefreshCw className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Transferencias Pendientes</p>
                <p className="text-2xl font-bold text-amber-600">
                  {pendingTransfers.length}
                </p>
              </div>
            </div>
            {pendingTransfers.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-gray-500">
                  Total: {formatCurrency(
                    pendingTransfers.reduce((sum, t) => sum + (t.amount || 0), 0)
                  )}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Cuenta Bancaria */}
        {bankAccount ? (
          <div className="bg-white rounded-xl shadow-sm border mb-8">
            <div className="p-6 border-b">
              <div className="flex items-center gap-3">
                <Building2 className="w-6 h-6" style={{ color: '#003594' }} />
                <h2 className="text-xl font-bold" style={{ color: '#051C2C' }}>
                  Cuenta Bancaria Configurada
                </h2>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Banco</label>
                  <p className="text-lg font-semibold" style={{ color: '#051C2C' }}>
                    {bankAccount.bank_name}
                  </p>
                  <p className="text-sm text-gray-500">Código: {bankAccount.bank_code}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Cuenta</label>
                  <p className="text-lg font-semibold capitalize" style={{ color: '#051C2C' }}>
                    {bankAccount.account_type}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Número de Cuenta</label>
                  <p className="text-lg font-mono font-semibold" style={{ color: '#003594' }}>
                    {bankAccount.account_number}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Titular</label>
                  <p className="text-lg font-semibold" style={{ color: '#051C2C' }}>
                    {bankAccount.account_holder_name}
                  </p>
                  <p className="text-sm text-gray-500">{bankAccount.account_holder_id}</p>
                </div>
              </div>
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  ℹ️ Esta cuenta recibirá las transferencias automáticas de dispersión. 
                  Si necesitas modificarla, contacta al administrador del sistema.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-amber-900 mb-1">
                  Cuenta Bancaria No Configurada
                </h3>
                <p className="text-sm text-amber-800">
                  Aún no tienes una cuenta bancaria configurada para recibir transferencias.
                  Por favor contacta al administrador del sistema para configurar tu cuenta.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Sistema de Dispersión */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6 mb-8">
          <h3 className="font-semibold text-lg mb-4" style={{ color: '#051C2C' }}>
            📅 Sistema de Dispersión Automática
          </h3>
          <p className="text-gray-700 mb-4">
            Tus fondos se transfieren automáticamente a tu cuenta bancaria 4 veces al día, 
            de lunes a viernes.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {pendingBalance?.next_dispersion_times?.map((time) => (
              <div key={time} className="bg-white rounded-lg p-3 text-center border border-blue-100">
                <Clock className="w-5 h-5 mx-auto mb-2 text-blue-600" />
                <p className="text-2xl font-bold" style={{ color: '#003594' }}>{time}</p>
                <p className="text-xs text-gray-600 mt-1">Hora de Caracas</p>
              </div>
            )) || (
              <>
                <div className="bg-white rounded-lg p-3 text-center border border-blue-100">
                  <p className="text-2xl font-bold" style={{ color: '#003594' }}>09:00</p>
                  <p className="text-xs text-gray-600 mt-1">AM</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center border border-blue-100">
                  <p className="text-2xl font-bold" style={{ color: '#003594' }}>12:00</p>
                  <p className="text-xs text-gray-600 mt-1">PM</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center border border-blue-100">
                  <p className="text-2xl font-bold" style={{ color: '#003594' }}>03:00</p>
                  <p className="text-xs text-gray-600 mt-1">PM</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center border border-blue-100">
                  <p className="text-2xl font-bold" style={{ color: '#003594' }}>06:00</p>
                  <p className="text-xs text-gray-600 mt-1">PM</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Historial de Transferencias */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="p-6 border-b">
            <h2 className="text-xl font-bold" style={{ color: '#051C2C' }}>
              Historial de Transferencias
            </h2>
          </div>
          
          {transferHistory.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <DollarSign className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-600 mb-2">No hay transferencias registradas</p>
              <p className="text-sm text-gray-500">
                Las transferencias aparecerán aquí una vez que tu balance supere el mínimo requerido.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Referencia
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Monto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cuenta Destino
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ref. Banco Plaza
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {transferHistory.map((transfer) => (
                    <tr key={transfer.transfer_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-900">
                            {formatDate(transfer.created_at)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-mono text-gray-600">
                          {transfer.transfer_id?.slice(0, 8)}...
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-bold text-green-600">
                          {formatCurrency(transfer.amount)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <p className="font-medium text-gray-900">
                            {transfer.destination_account?.bank_name || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500 font-mono">
                            {transfer.destination_account?.account_number || 'N/A'}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(transfer.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {transfer.banco_plaza_reference ? (
                          <span className="text-xs font-mono text-gray-600">
                            {transfer.banco_plaza_reference}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">---</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info Footer */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="font-semibold mb-3" style={{ color: '#051C2C' }}>
            ℹ️ Información sobre Transferencias
          </h3>
          <div className="space-y-2 text-sm text-gray-700">
            <p>
              • Las transferencias se ejecutan automáticamente 4 veces al día (9am, 12pm, 3pm, 6pm) 
              de lunes a viernes.
            </p>
            <p>
              • Solo se transfieren balances que superen el monto mínimo configurado 
              ({pendingBalance ? formatCurrency(pendingBalance.minimum_transfer_amount) : '---'}).
            </p>
            <p>
              • Las transferencias se realizan mediante el sistema de Crédito Inmediato de Banco Plaza.
            </p>
            <p>
              • Si una transferencia falla, se reintentará automáticamente en el siguiente ciclo 
              (máximo 3 intentos).
            </p>
            <p>
              • Los fondos se transfieren a la cuenta bancaria que el administrador configuró para ti.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
